import React from "react";
import PropTypes from "prop-types";

// Import default icons
import checkCircle from "assets/icons/check-circle.svg";
import slashCircle from "assets/icons/slash-circle.svg";
import infoCircle from "assets/icons/info-circle.svg";
import alertCircle from "assets/icons/alert-circle.svg";

// Default icons mapping for variants and types
const defaultIcons = {
  // Variants
  primary: infoCircle,
  success: checkCircle,
  warning: alertCircle,
  danger: slashCircle,
  info: infoCircle,
  error: slashCircle,
  loading: infoCircle,
  custom: infoCircle,
};

// Map notification types to variants
const typeToVariantMap = {
  success: "success",
  error: "danger",
  loading: "info",
  custom: "primary",
};

/**
 * Get the appropriate variant based on type or variant prop
 * @param {string} type - The notification type (success, error, loading, custom)
 * @param {string} variant - The direct variant (primary, success, warning, danger, info)
 * @returns {string} The resolved variant
 */
const getVariant = (type, variant) => {
  // If variant is provided, use it directly
  if (variant) return variant;
  // If type is provided, map it to a variant
  if (type) return typeToVariantMap[type] || "primary";
  // Default to primary if neither is provided
  return "primary";
};

/**
 * Toast Notification Component
 *
 * @param {Object} props - Component props
 * @param {string} props.icon - Icon image source
 * @param {string} props.title - Title text
 * @param {string} props.description - Description text
 * @param {string} props.primaryButtonText - Text for primary button (optional)
 * @param {string} props.secondaryButtonText - Text for secondary button (default: "ปิดหน้าต่าง")
 * @param {Function} props.onPrimaryClick - Function to call when primary button is clicked
 * @param {Function} props.onSecondaryClick - Function to call when secondary button is clicked
 * @param {string} props.variant - Color variant (primary, success, warning, danger, info) (default: "primary")
 * @returns {JSX.Element} Toast notification component
 */
const ToastNotification = ({ icon, title, description, primaryButtonText, secondaryButtonText = "ปิดหน้าต่าง", onPrimaryClick, onSecondaryClick, variant, type }) => {
  const finalVariant = getVariant(type, variant);

  const variantClass = finalVariant === "danger" ? "error" : finalVariant;

  // 3. Get the appropriate icon (use provided icon or default based on variant)
  const iconSrc = icon || defaultIcons[finalVariant] || defaultIcons.primary;
  return (
    <div className="react-toast-notifications__toast bg-white rounded-xl shadow max-w-96">
      <div className="d-flex gap-3 p-2">
        <div className="">
          <div className={`toast-icon ${variantClass}`}>
            <div className={`toast-ring ${variantClass} toast-animate-ping`}>
              <img src={iconSrc} alt="notification" />
            </div>
          </div>
        </div>
        <div className="flex-grow-1 toast-content d-flex flex-column gap-4 pr-2">
          <div className="d-flex flex-column gap-2">
            <h3 className="fw-semibold text-gray-900 mb-0">{title}</h3>
            <h3 className="text-gray-600">{description}</h3>
          </div>
          <div className="d-flex gap-3">
            <button
              className="link"
              onClick={() => {
                if (onSecondaryClick) {
                  onSecondaryClick();
                }
              }}
            >
              {secondaryButtonText}
            </button>
            {primaryButtonText && (
              <button className={`link ${finalVariant}`} onClick={onPrimaryClick}>
                {primaryButtonText}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

ToastNotification.propTypes = {
  icon: PropTypes.string,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  primaryButtonText: PropTypes.string,
  secondaryButtonText: PropTypes.string,
  onPrimaryClick: PropTypes.func,
  onSecondaryClick: PropTypes.func.isRequired,
  variant: PropTypes.oneOf(["primary", "success", "warning", "danger", "info"]),
  type: PropTypes.oneOf(["success", "error", "loading", "custom"]),
  icon: PropTypes.string,
};

export default ToastNotification;
